import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export const THUISBEZORGD_ORDER_URL = 'https://www.thuisbezorgd.nl/menu/ams-food-market';

const OrderModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const orderOptions = [
    {
      name: 'Thuisbezorgd',
      url: THUISBEZORGD_ORDER_URL,
      description: t('orderModal.thuisbezorgdDesc'),
      icon: '🚴',
      color: 'from-orange-500 to-red-500',
    },
    /*
    {
      name: 'Sitedish',
      url: 'https://amsfoodmarket.sitedish.shop/#menu',
      description: t('orderModal.sitedishDesc'),
      icon: '🛒',
      color: 'from-green-500 to-emerald-500',
    }
    */
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-green-400 to-primary p-6 relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {t('orderModal.title')}
                </h2>
                <p className="text-white/90 text-sm">
                  {t('orderModal.subtitle')}
                </p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {orderOptions.map((option, index) => (
                  <motion.a
                    key={option.name}
                    href={option.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="relative overflow-hidden rounded-xl border-2 border-gray-200 hover:border-primary transition-all duration-300 hover:shadow-lg">
                      {/* Gradient background on hover */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                      
                      <div className="relative p-5 flex items-start gap-4">
                        <div className="text-4xl flex-shrink-0 transform group-hover:scale-110 transition-transform">
                          {option.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-lg text-gray-800 group-hover:text-primary transition-colors">
                              {option.name}
                            </h3>
                            <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 pb-6">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="w-full"
                >
                  {t('orderModal.cancel')}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OrderModal;

